import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Target, Eye, Heart, ShieldCheck, Users, Handshake, MapPin } from "lucide-react";

const VALUES = [
  { icon: ShieldCheck, title: "Trust", desc: "We recommend only verified, RERA-registered projects." },
  { icon: Eye, title: "Transparency", desc: "Clear pricing and honest advice with no hidden agenda." },
  { icon: Users, title: "Customer-First Advice", desc: "Your interests always come before any transaction." },
  { icon: Handshake, title: "Long-Term Relationships", desc: "We build lasting relationships, not one-time deals." },
];

const AREAS = ["Sarjapur Road", "Whitefield", "Electronic City", "Bannerghatta Road", "Varthur", "Hoskote", "North Bangalore"];

export default function About() {
  const navigate = useNavigate();
  const [team, setTeam] = useState([]);
  useEffect(() => { api.get("/team").then((r) => setTeam(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <SEO title="About Mathura Homes | Real Estate Channel Partner in Bangalore" description="Learn about Mathura Homes — a trusted property consultant and channel partner helping Bangalore homebuyers and investors make confident decisions." />
      <section className="bg-navy py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <span className="inline-block h-1 w-16 bg-gold mb-6" />
          <h1 className="font-display text-4xl md:text-5xl text-white">About Mathura Homes</h1>
          <p className="text-gray-300 mt-5 leading-relaxed">
            Mathura Homes is a real estate channel partner and property consultant based in Bangalore. We help homebuyers, investors and NRIs discover verified residential projects and navigate the entire purchase journey with clarity and confidence.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-2 gap-10">
        <div className="p-8 rounded-2xl bg-muted border border-gray-100">
          <Target className="w-10 h-10 text-gold mb-4" strokeWidth={1.5} />
          <h2 className="font-display text-2xl text-navy mb-3">Our Mission</h2>
          <p className="text-muted-foreground leading-relaxed">To simplify property buying in Bangalore by offering unbiased, personalised guidance that empowers our clients to make well-informed decisions.</p>
        </div>
        <div className="p-8 rounded-2xl bg-muted border border-gray-100">
          <Eye className="w-10 h-10 text-gold mb-4" strokeWidth={1.5} />
          <h2 className="font-display text-2xl text-navy mb-3">Our Vision</h2>
          <p className="text-muted-foreground leading-relaxed">To be Bangalore's most trusted property advisory firm — known for transparency, integrity and putting customers first.</p>
        </div>
      </section>

      <section className="bg-muted py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12"><h2 className="font-display text-3xl md:text-4xl text-navy">Our Values</h2></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v) => (
              <div key={v.title} className="p-7 rounded-2xl bg-white border border-gray-100 shadow-card">
                <span className="inline-flex p-3 rounded-xl bg-gold/15 text-gold-dark mb-4"><v.icon className="w-6 h-6" strokeWidth={1.5} /></span>
                <h3 className="font-display text-xl text-navy mb-2">{v.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team - editable from admin */}
      {team.length > 0 && (
      <section className="bg-muted py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12"><h2 className="font-display text-3xl md:text-4xl text-navy">Our Team</h2></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {team.map((m) => (
              <div key={m.id} className="p-6 rounded-2xl bg-white border border-gray-100 text-center" data-testid={`team-${m.id}`}>
                {m.photo ? <img src={m.photo} alt={m.name} loading="lazy" className="w-20 h-20 rounded-full object-cover mx-auto" /> : <div className="w-20 h-20 rounded-full bg-navy/10 mx-auto flex items-center justify-center"><Users className="w-8 h-8 text-navy/40" /></div>}
                <p className="font-semibold text-navy mt-4">{m.name}</p>
                <p className="text-xs text-gold">{m.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="font-display text-3xl text-navy mb-6 text-center">Service Areas</h2>
        <div className="flex flex-wrap justify-center gap-3">
          {AREAS.map((a) => <span key={a} className="px-5 py-2.5 rounded-full bg-muted border border-gray-100 text-navy font-medium flex items-center gap-2"><MapPin className="w-4 h-4 text-gold" /> {a}</span>)}
        </div>
      </section>

      <section className="bg-navy py-16 text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="font-display text-3xl text-white">Let's Find Your Perfect Property</h2>
          <p className="text-gray-300 mt-3">Schedule a consultation with our advisory team today.</p>
          <Button onClick={() => navigate("/contact")} className="mt-6 bg-gold text-navy hover:bg-gold-dark font-semibold px-7 py-6">Schedule a Consultation</Button>
        </div>
      </section>
    </div>
  );
}
